import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, PauseCircle, RotateCcw, Search, Shield, UserX, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { getApiErrorMessage } from '../../lib/apiError';
import AdminUserDetailModal from '../../components/admin/AdminUserDetailModal';

const fullName = (user) => (
  [user?.profile?.first_name || user?.profile?.firstName, user?.profile?.last_name || user?.profile?.lastName]
    .filter(Boolean)
    .join(' ') || user?.email || 'Supervisor'
);

const statusStyles = {
  active: 'bg-green-100 text-green-700 border-green-200',
  pending_verification: 'bg-amber-100 text-amber-800 border-amber-200',
  pending_approval: 'bg-amber-100 text-amber-800 border-amber-200',
  suspended: 'bg-red-100 text-red-700 border-red-200',
  deactivated: 'bg-gray-100 text-gray-600 border-gray-200',
};

const AdminSupervisorsPage = () => {
  const { authApi } = useAuth();
  const { visits, careOfficers, fetchData } = useApp();
  const [supervisors, setSupervisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailUserId, setDetailUserId] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  const loadSupervisors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authApi.get('/admin/users?role=supervisor&limit=100');
      setSupervisors(response.data.users || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Unable to load supervisors'));
    } finally {
      setLoading(false);
    }
  }, [authApi]);

  useEffect(() => {
    loadSupervisors();
  }, [loadSupervisors]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return supervisors.filter((supervisor) => {
      if (!query) return true;
      return fullName(supervisor).toLowerCase().includes(query) || String(supervisor.email || '').toLowerCase().includes(query);
    });
  }, [search, supervisors]);

  const runAction = async (user, action) => {
    setActionLoading(`${action}-${user.id}`);
    try {
      const endpoint = action === 'reactivate'
        ? `/admin/users/${user.id}/reactivate`
        : `/admin/users/${user.id}/${action}`;
      await authApi.post(endpoint, { reason: `${action} by admin` });
      const labels = { suspend: 'suspended', deactivate: 'deactivated', reactivate: 'reactivated' };
      toast.success(`Supervisor ${labels[action] || 'updated'}`);
      await loadSupervisors();
      await fetchData({ force: true });
      setSelected(null);
    } catch (error) {
      toast.error(getApiErrorMessage(error, `Unable to ${action} supervisor`));
    } finally {
      setActionLoading('');
    }
  };

  const stats = {
    total: supervisors.length,
    active: supervisors.filter(item => item.status === 'active').length,
    suspended: supervisors.filter(item => item.status === 'suspended').length,
    deactivated: supervisors.filter(item => item.status === 'deactivated').length,
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-supervisors-page">
      <div className="border-b border-[#E8E6E3] bg-[#FAFAF7]">
        <div className="mx-auto max-w-7xl px-6 py-8">
          <h1 className="font-serif text-3xl font-semibold text-[#1A1A1A]">Supervisors</h1>
          <p className="mt-1 text-sm text-[#5A5A5A]">Admin-only oversight of supervisor accounts and access status.</p>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ['Total supervisors', stats.total],
            ['Active', stats.active],
            ['Suspended', stats.suspended],
            ['Deactivated', stats.deactivated],
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-5">
              <p className="font-serif text-3xl font-semibold text-[#1A1A1A]">{value}</p>
              <p className="mt-1 text-xs font-medium uppercase tracking-[0.12em] text-[#5A5A5A]">{label}</p>
            </div>
          ))}
        </div>

        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] px-4 py-3">
          <Search className="h-4 w-4 text-[#5A5A5A]" />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search supervisors..."
            className="h-9 flex-1 bg-transparent text-sm outline-none"
          />
        </div>

        <div className="overflow-hidden rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7]">
          {loading ? (
            <div className="flex items-center justify-center p-12 text-[#5A5A5A]">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Loading supervisors...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-[#E8E6E3] bg-white text-left text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">
                  <tr>
                    <th className="px-5 py-4">Supervisor</th>
                    <th className="px-5 py-4">Region</th>
                    <th className="px-5 py-4">Managed Officers</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E6E3]">
                  {filtered.map((supervisor) => {
                    const managedIds = supervisor.profile?.care_officers_managed || supervisor.profile?.careOfficersManaged || [];
                    return (
                      <tr key={supervisor.id} className="hover:bg-white">
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => setDetailUserId(supervisor.id)}
                            className="text-left font-medium text-[#1A1A1A] hover:text-[#9F1D35]"
                          >
                            {fullName(supervisor)}
                          </button>
                          <p className="text-sm text-[#5A5A5A]">{supervisor.email}</p>
                        </td>
                        <td className="px-5 py-4 text-sm text-[#5A5A5A]">{supervisor.profile?.assigned_region || supervisor.profile?.assignedRegion || '-'}</td>
                        <td className="px-5 py-4 text-sm text-[#1A1A1A]">{managedIds.length}</td>
                        <td className="px-5 py-4">
                          <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${statusStyles[supervisor.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                            {supervisor.status}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-2">
                            <button type="button" onClick={() => setSelected(supervisor)} className="rounded-full px-3 py-1.5 text-sm font-medium text-[#9F1D35] hover:bg-[#F5E8EA]">View</button>
                            {supervisor.status === 'active' ? (
                              <button type="button" onClick={() => runAction(supervisor, 'suspend')} disabled={Boolean(actionLoading)} className="rounded-full px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">Suspend</button>
                            ) : (
                              <button type="button" onClick={() => runAction(supervisor, 'reactivate')} disabled={Boolean(actionLoading)} className="rounded-full px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-50 disabled:opacity-50">Reactivate</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {filtered.length === 0 && <div className="p-12 text-center text-sm text-[#5A5A5A]">No supervisors found.</div>}
            </div>
          )}
        </div>
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl border border-[#E8E6E3] bg-[#FAFAF7] p-6">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#9F1D35]">Supervisor record</p>
                <h2 className="mt-2 font-serif text-3xl font-semibold text-[#1A1A1A]">{fullName(selected)}</h2>
                <p className="mt-1 text-sm text-[#5A5A5A]">{selected.email}</p>
              </div>
              <button type="button" onClick={() => setSelected(null)} className="rounded-full border border-[#D8D5D0] p-2 text-[#5A5A5A]" aria-label="Close supervisor details">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <Shield className="mb-3 h-5 w-5 text-[#9F1D35]" />
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Status</p>
                <p className="mt-1 font-semibold text-[#1A1A1A]">{selected.status}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Managed Officers</p>
                <p className="mt-1 font-semibold text-[#1A1A1A]">{(selected.profile?.care_officers_managed || selected.profile?.careOfficersManaged || []).length}</p>
              </div>
              <div className="rounded-2xl border border-[#E8E6E3] bg-white p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#5A5A5A]">Visible Visits</p>
                <p className="mt-1 font-semibold text-[#1A1A1A]">{visits.length}</p>
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-[#E8E6E3] bg-white p-5">
              <h3 className="font-serif text-xl font-semibold text-[#1A1A1A]">Profile details</h3>
              <div className="mt-4 grid gap-3 text-sm md:grid-cols-2">
                <p><span className="text-[#5A5A5A]">Phone:</span> {selected.profile?.phone || selected.profile?.primaryPhone || 'Not set'}</p>
                <p><span className="text-[#5A5A5A]">Region:</span> {selected.profile?.assigned_region || selected.profile?.assignedRegion || 'Not set'}</p>
                <p><span className="text-[#5A5A5A]">Created:</span> {selected.created_at ? new Date(selected.created_at).toLocaleDateString() : 'Unknown'}</p>
                <p><span className="text-[#5A5A5A]">Care officers in system:</span> {careOfficers.length}</p>
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t border-[#E8E6E3] pt-5 sm:flex-row sm:justify-end">
              {selected.status === 'active' ? (
                <>
                  <button type="button" disabled={Boolean(actionLoading)} onClick={() => runAction(selected, 'suspend')} className="inline-flex items-center justify-center gap-2 rounded-full border border-red-300 px-5 py-3 text-sm font-medium text-red-700 disabled:opacity-50">
                    <PauseCircle className="h-4 w-4" />
                    Suspend Supervisor
                  </button>
                  <button type="button" disabled={Boolean(actionLoading)} onClick={() => runAction(selected, 'deactivate')} className="inline-flex items-center justify-center gap-2 rounded-full bg-red-700 px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-50">
                    <UserX className="h-4 w-4" />
                    Deactivate Supervisor
                  </button>
                </>
              ) : (
                <button type="button" disabled={Boolean(actionLoading)} onClick={() => runAction(selected, 'reactivate')} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#9F1D35] px-5 py-3 text-sm font-medium text-[#FAFAF7] disabled:opacity-50">
                  <RotateCcw className="h-4 w-4" />
                  Reactivate Supervisor
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      <AdminUserDetailModal
        userId={detailUserId}
        open={Boolean(detailUserId)}
        onOpenChange={(open) => !open && setDetailUserId('')}
      />
    </div>
  );
};

export default AdminSupervisorsPage;
